import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { catLabel, catLines, fmtDate, useApi } from "../lib/api";
import { MaskLines, Reveal } from "../components/Reveal";
import { LeadGrid } from "../components/home/LeadGrid";
import { FeatureSpread } from "../components/home/FeatureSpread";
import { RiotBanner } from "../components/home/RiotBanner";
import { Seo } from "../components/Seo";

const INTRO = {
  fashion: "Clothes as language: the looks, labels and people rewriting the rules from the street up.",
  culture: "Scenes, sounds and nights out. The places young people gather and the stories they bring home.",
  art: "Studios, shows, photographers and the new names worth knowing before everyone else does.",
  beauty: "Faces, nails, hair and rituals. Beauty as play, armour and self-portrait.",
  riot: "Protest, noise and resistance. Youth culture with the volume all the way up.",
};
const ORDER = { fashion: 1, culture: 2, art: 3, beauty: 4, riot: 5 };
const CELL = "p-5 sm:p-6 border-b border-ink/15";

// Card like a printed picture page: clean photo, small mono caption, headline in the issue's type.
const GridCard = ({ a, n, testId }) => (
  <Link to={`/article/${a.slug}`} className="group block" data-testid={testId}>
    <div className="img-zoom relative overflow-hidden aspect-[3/4] bg-paper">
      <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover object-top" />
    </div>
    <p className="kicker text-[11px] text-ink/70 mt-2.5"><span className="text-pink">{String(n).padStart(2, "0")}</span> · {fmtDate(a.published)} · {a.read_time} min</p>
    <h3 className="dz-type text-2xl sm:text-[1.7rem] mt-3 group-hover:text-pink transition-colors">{a.title}</h3>
  </Link>
);

const Grid = ({ items, start }) =>
  items.length ? (
    <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4">
        {items.map((a, i) => (
          <Reveal key={a.slug} delay={(i % 4) * 0.06} className={CELL}>
            <GridCard a={a} n={start + i} testId={`category-card-${start + i - 1}`} />
          </Reveal>
        ))}
      </div>
    </div>
  ) : null;

// Like the printed section page: giant pink number top right, the section name huge at the bottom.
const Header = ({ cat, total }) => (
  <section className="max-w-[1440px] mx-auto px-5 sm:px-10 min-h-[48svh] lg:min-h-[70svh] flex flex-col justify-between border-b border-ink pb-6" data-testid="category-header">
    <div className="flex justify-between items-start gap-6">
      <p className="kicker text-ink/70 pt-2" data-testid="category-count">{total} stories</p>
      <span className="wordmark text-pink text-[clamp(7rem,22vw,20rem)] leading-[0.78]" aria-hidden="true">{String(ORDER[cat] ?? 0).padStart(2, "0")}</span>
    </div>
    <div className="grid lg:grid-cols-12 gap-6 items-end mt-10">
      <h1 className={`lg:col-span-8 display ${cat === "art" ? "text-[clamp(3.4rem,8.5vw,8.5rem)]" : "text-[clamp(4rem,14vw,13rem)]"} leading-[0.8] text-ink`} key={cat}>
        <MaskLines lines={catLines(cat)} testId="category-title" />
      </h1>
      <p className="lg:col-span-4 font-serif text-xl sm:text-2xl leading-snug" data-testid="category-intro">{INTRO[cat]}</p>
    </div>
  </section>
);

const TagFilters = ({ tags, tag, setTag }) => (
  <div className="max-w-[1440px] mx-auto px-5 sm:px-10 flex flex-wrap gap-2 py-8" data-testid="category-tag-filters">
    {[null, ...tags].map((t) => (
      <button
        key={t ?? "all"}
        onClick={() => setTag(t)}
        className={`kicker px-4 py-2 border border-ink transition-colors ${tag === t ? "bg-pink text-ink" : "hover:bg-paper"}`}
        data-testid={t ? `tag-filter-${t.replace(/\s+/g, "-")}` : "tag-filter-all"}
      >
        {t ?? "All"}
      </button>
    ))}
  </div>
);

export default function Category() {
  const { cat } = useParams();
  const { data, isLoading } = useApi("/articles", { category: cat, limit: 100 });
  const [tag, setTag] = useState(null);
  const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data]);
  const tags = useMemo(() => [...new Set(items.flatMap((a) => a.tags ?? []))].slice(0, 14), [items]);
  const riot = cat === "riot";
  const shown = (tag ? items.filter((a) => a.tags?.includes(tag)) : items).slice(riot && !tag ? 1 : 0);
  const [lead, ...after] = shown;
  const pair = after.slice(0, 2);
  const edit = after.slice(2, 7);
  const rest = after.slice(7);
  const feature = rest[8];

  return (
    <main className="pt-32 pb-24" data-testid="category-page">
      <Seo
        title={`${riot ? "RIOT" : (catLabel(cat).charAt(0).toUpperCase() + catLabel(cat).slice(1))} — Youth Culture Stories | YOUTH`}
        description={INTRO[cat] ?? "Stories from YOUTH, the youth culture magazine."}
        path={`/category/${cat}`}
        noindex={!INTRO[cat]}
      />
      <Header cat={cat} total={data?.total ?? 0} />
      {riot && !isLoading && <div className="mt-10"><RiotBanner a={items[0]} linkToSection={false} /></div>}
      {(!riot || tags.length > 0) && <TagFilters tags={tags} tag={tag} setTag={setTag} />}
      {isLoading && <p className="kicker py-20 text-center">Loading…</p>}
      <LeadGrid lead={lead} pair={pair} edit={edit} heading={false} />
      <Grid items={rest.slice(0, 8)} start={1} />
      {feature && <div className="my-16 sm:my-24"><FeatureSpread a={feature} /></div>}
      <Grid items={rest.slice(9)} start={10} />
      {!isLoading && !shown.length && !riot && <p className="font-serif italic text-2xl text-center py-20" data-testid="category-empty">No stories here yet.</p>}
    </main>
  );
}
